import React, { useMemo, useState, useEffect } from "react";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * Reusable Fee Calculator (kBola "new style")
 */
export default function FeeCalculator({
                                          currencyOptions = ["USD", "CAD", "EUR"],
                                          defaultCurrency = "USD",
                                          defaultAmount = 5.0,
                                          defaultAppFeePct = 5,
                                          defaultStripePct = 2.9,
                                          defaultStripeFixed = 0.3,
                                          onChange,
                                          title,
                                          colors,
                                      }) {
    const { t } = useTranslation();

    const resolvedTitle = title || t("feeCalculator.defaultTitle");

    const [currency, setCurrency] = useState(defaultCurrency);
    const [amountPaid, setAmountPaid] = useState(defaultAmount);
    const [appFeePct, setAppFeePct] = useState(defaultAppFeePct);
    const [stripePct, setStripePct] = useState(defaultStripePct);
    const [stripeFixed, setStripeFixed] = useState(defaultStripeFixed);

    const fmt = useMemo(
        () =>
            new Intl.NumberFormat(undefined, {
                style: "currency",
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }),
        [currency]
    );

    const calc = useMemo(() => {
        const amt = isNaN(amountPaid) ? 0 : Math.max(0, Number(amountPaid));
        const appFee = ((isNaN(appFeePct) ? 0 : Number(appFeePct)) / 100) * amt;
        const toLeagueBeforeStripe = Math.max(0, amt - appFee);
        const stripeFee =
            ((isNaN(stripePct) ? 0 : Number(stripePct)) / 100) * toLeagueBeforeStripe +
            (isNaN(stripeFixed) ? 0 : Number(stripeFixed));
        const leagueNet = Math.max(0, toLeagueBeforeStripe - stripeFee);

        return {
            currency,
            amountPaid: amt,
            appFeePct: Number(appFeePct) || 0,
            stripePct: Number(stripePct) || 0,
            stripeFixed: Number(stripeFixed) || 0,
            appFee,
            toLeagueBeforeStripe,
            stripeFee,
            leagueNet,
            fmt,
        };
    }, [currency, amountPaid, appFeePct, stripePct, stripeFixed, fmt]);

    useEffect(() => {
        if (onChange) {
            const { fmt: _omit, ...payload } = calc;
            onChange(payload);
        }
    }, [calc, onChange]);

    const scopeId = useMemo(() => {
        const safe = String(resolvedTitle || "fee-calculator")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-fee-${safe || "x"}`;
    }, [resolvedTitle]);

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            body: { padding: 14 },
            label: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
                display: "inline-block",
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
            },
            grid: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            full: { gridColumn: "1 / -1" },
            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            sectionTitle: {
                margin: 0,
                fontWeight: 1000,
                color: "#14151a",
                fontSize: 13.5,
            },
            sectionSub: {
                margin: "4px 0 0 0",
                fontWeight: 700,
                color: "rgba(20,21,26,0.60)",
                fontSize: 12.5,
            },
            tableWrap: {
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
                marginTop: 10,
            },
            row: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "10px 12px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            rowFirst: { borderTop: "none" },
            rowLabel: { fontWeight: 900, color: "rgba(20,21,26,0.75)", fontSize: 12.8 },
            rowValue: { fontWeight: 1000, color: "#14151a", fontSize: 12.8, whiteSpace: "nowrap" },
            totalRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "12px 12px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            totalLabel: { fontWeight: 1000, color: "#14151a", fontSize: 13 },
            totalValue: { fontWeight: 1100, color: "#14151a", fontSize: 13, whiteSpace: "nowrap" },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "rgba(20,21,26,0.75)",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    return (
        <div id={scopeId} style={ui.wrap}>
            <div style={ui.header}>
                <div style={ui.titleWrap}>
                    <span style={ui.badge}>{t("feeCalculator.badge")}</span>
                    <div style={{ minWidth: 0 }}>
                        <h3 style={ui.title} title={resolvedTitle}>
                            {resolvedTitle}
                        </h3>
                        <p style={ui.sub}>{t("feeCalculator.subtitle")}</p>
                    </div>
                </div>

                <span style={ui.chip} title={t("feeCalculator.currencyInUse")}>
                    <i className="fas fa-coins" />
                    {currency}
                </span>
            </div>

            <div style={ui.body}>
                <div style={ui.grid}>
                    <div style={ui.full}>
                        <label style={ui.label}>{t("feeCalculator.currency")}</label>
                        <select
                            className="form-control"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            style={ui.input}
                        >
                            {currencyOptions.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={ui.label}>{t("feeCalculator.amountPaid")}</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control"
                            value={isNaN(amountPaid) ? "" : amountPaid}
                            onChange={(e) => setAmountPaid(parseFloat(e.target.value))}
                            style={ui.input}
                        />
                    </div>

                    <div>
                        <label style={ui.label}>{t("feeCalculator.appFeePct")}</label>
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            className="form-control"
                            value={appFeePct}
                            onChange={(e) => setAppFeePct(parseFloat(e.target.value))}
                            readOnly
                            style={{ ...ui.input, background: "rgba(0,0,0,0.015)" }}
                        />
                    </div>

                    <div>
                        <label style={ui.label}>{t("feeCalculator.stripePct")}</label>
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            className="form-control"
                            value={stripePct}
                            onChange={(e) => setStripePct(parseFloat(e.target.value))}
                            readOnly
                            style={{ ...ui.input, background: "rgba(0,0,0,0.015)" }}
                        />
                    </div>

                    <div>
                        <label style={ui.label}>{t("feeCalculator.stripeFixed")}</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control"
                            value={stripeFixed}
                            onChange={(e) => setStripeFixed(parseFloat(e.target.value))}
                            readOnly
                            style={{ ...ui.input, background: "rgba(0,0,0,0.015)" }}
                        />
                    </div>
                </div>

                <div style={{ marginTop: 12 }}>
                    <div style={ui.sectionCard}>
                        <p style={ui.sectionTitle}>{t("feeCalculator.breakdownTitle")}</p>
                        <p style={ui.sectionSub}>{t("feeCalculator.breakdownSubtitle")}</p>

                        <div style={ui.tableWrap}>
                            <div style={{ ...ui.row, ...ui.rowFirst }}>
                                <div style={ui.rowLabel}>{t("feeCalculator.userPays")}</div>
                                <div style={ui.rowValue}>{calc.fmt.format(calc.amountPaid)}</div>
                            </div>

                            <div style={ui.row}>
                                <div style={ui.rowLabel}>
                                    {t("feeCalculator.kbolaFee", { pct: calc.appFeePct })}
                                </div>
                                <div style={ui.rowValue}>− {calc.fmt.format(calc.appFee)}</div>
                            </div>

                            <div style={ui.row}>
                                <div style={ui.rowLabel}>{t("feeCalculator.amountBeforeStripe")}</div>
                                <div style={ui.rowValue}>{calc.fmt.format(calc.toLeagueBeforeStripe)}</div>
                            </div>

                            <div style={ui.row}>
                                <div style={ui.rowLabel}>
                                    {t("feeCalculator.stripeFee", {
                                        pct: calc.stripePct,
                                        fixed: calc.fmt.format(calc.stripeFixed),
                                    })}
                                </div>
                                <div style={ui.rowValue}>− {calc.fmt.format(calc.stripeFee)}</div>
                            </div>

                            <div style={ui.totalRow}>
                                <div style={ui.totalLabel}>{t("feeCalculator.organizationReceives")}</div>
                                <div style={ui.totalValue}>{calc.fmt.format(calc.leagueNet)}</div>
                            </div>
                        </div>

                        <div
                            style={{
                                marginTop: 10,
                                fontWeight: 700,
                                fontSize: 12.5,
                                color: "rgba(20,21,26,0.60)",
                            }}
                        >
                            {t("feeCalculator.stripeAccountNote")}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        #${scopeId}{
          font-family: 'Poppins', sans-serif;
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        #${scopeId} .form-control:focus,
        #${scopeId} button:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
        }

        #${scopeId} select.form-control{
          appearance: auto;
        }
      `}</style>
        </div>
    );
}
